/**
 * ============================================================================
 * EXPERIMENT: Travel Rhythm Display (ab_travel_rhythm)
 *
 * Backend-driven AB test — variant decided server-side via API
 * `meta.experiment.travelRhythm`. No Amplitude experiment, no entry in
 * `feature-flags.json`: the variant is implicit — the feature renders only
 * when the API returns a rhythm value.
 *
 * PR: #TODO_AFTER_PR_CREATION_ab_travel_rhythm
 * Expected end date: 2026-07-22 (remove all AB_* files and BEGIN/END markers by then).
 *
 * Temporary code — will be REMOVED when the experiment concludes.
 * Find all experiment code: grep -rn "ab_travel_rhythm" src/
 * ============================================================================
 */
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    paletteColors,
    Stack,
    Typography,
} from '@evaneos/b2b-ui';
import { useIntl } from 'react-intl';

import { TravelRhythmValue } from '../../../../resources/dossier/types';

type Props = {
    value: TravelRhythmValue;
    onDismiss: () => void;
};

export function AB_TravelRhythmModalView({ value, onDismiss }: Readonly<Props>) {
    const { formatMessage } = useIntl();

    return (
        <Dialog
            open
            data-spec-id="travel-rhythm-modal"
            onClose={undefined}
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    backgroundColor: paletteColors.green[900],
                    color: paletteColors.neutral[0],
                    width: 477,
                    maxWidth: 'calc(100% - 64px)',
                    borderRadius: '8px',
                },
            }}
        >
            <DialogTitle sx={{ color: paletteColors.neutral[0], padding: '24px 30px 16px' }}>
                <Stack direction="column" spacing={2} alignItems="flex-start">
                    {/* TODO design-system: Figma "Offre en étoile" uses a lime green (#9FE870) not present in the Evaneos palette. Falling back to green[300] until the palette is extended. */}
                    <Chip
                        label={formatMessage({ id: 'dossier.travelRhythm.modal.badge' })}
                        sx={{
                            backgroundColor: paletteColors.green[300],
                            color: paletteColors.green[900],
                            fontWeight: 700,
                            fontSize: 14,
                            height: 32,
                        }}
                    />
                    <Stack direction="column" spacing={0}>
                        {value !== 'unknown' && (
                            <Typography
                                component="span"
                                sx={{
                                    color: paletteColors.neutral[0],
                                    fontWeight: 700,
                                    fontSize: '20px',
                                    lineHeight: '28px',
                                }}
                            >
                                {formatMessage({
                                    id: 'dossier.travelRhythm.modal.titlePrefix',
                                })}
                            </Typography>
                        )}
                        <Typography
                            component="span"
                            sx={{
                                color: paletteColors.neutral[0],
                                fontWeight: 700,
                                fontSize: '20px',
                                lineHeight: '28px',
                            }}
                        >
                            {formatMessage({ id: `dossier.travelRhythm.modal.title.${value}` })}
                        </Typography>
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent sx={{ padding: '16px 30px 0' }}>
                <Box
                    sx={{
                        backgroundColor: paletteColors.neutral[0],
                        color: paletteColors.neutral[900],
                        borderRadius: '4px',
                        border: `1px solid ${paletteColors.neutral[200]}`,
                        padding: '16px',
                    }}
                >
                    <Typography
                        variant="body2"
                        data-spec-id="travel-rhythm-modal-body"
                        sx={{ color: paletteColors.neutral[900] }}
                    >
                        {formatMessage({ id: `dossier.travelRhythm.modal.body.${value}` })}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ padding: '16px 30px 24px' }}>
                <Button
                    onClick={onDismiss}
                    data-spec-id="travel-rhythm-modal-cta"
                    variant="contained"
                    sx={{
                        backgroundColor: paletteColors.neutral[0],
                        color: paletteColors.neutral[900],
                        height: 40,
                        fontSize: 16,
                        fontWeight: 700,
                        '&:hover': { backgroundColor: paletteColors.neutral[100] },
                    }}
                >
                    {formatMessage({ id: 'dossier.travelRhythm.modal.cta' })}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
